import { ChangeDetectionStrategy, Component, OnDestroy, OnInit, computed, inject, signal } from '@angular/core';
import { FormsModule } from '@angular/forms';
import { RouterLink } from '@angular/router';
import { Button } from 'primeng/button';
import { Dialog } from 'primeng/dialog';
import { ToggleSwitch } from 'primeng/toggleswitch';
import { ConcursoFotografiasService, FotografiaAdmin } from '../services/concurso-fotografias.service';

@Component({
  selector: 'app-administracao-fotografias',
  standalone: true,
  imports: [FormsModule, RouterLink, Button, Dialog, ToggleSwitch],
  templateUrl: './administracao.html',
  styleUrl: './administracao.css',
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class AdministracaoFotografiasComponent implements OnInit, OnDestroy {
  private static readonly TAMANHO_MAXIMO_FOTO = 10 * 1024 * 1024;
  private readonly service = inject(ConcursoFotografiasService);
  protected readonly fotos = signal<FotografiaAdmin[]>([]);
  protected readonly carregando = signal(true);
  protected readonly salvando = signal(false);
  protected readonly erro = signal('');
  protected readonly sucesso = signal('');
  protected readonly modalCadastro = signal(false);
  protected readonly editando = signal<FotografiaAdmin | null>(null);
  protected readonly titulo = signal('');
  protected readonly descricao = signal('');
  protected readonly arquivo = signal<File | null>(null);
  protected readonly preview = signal<string | null>(null);
  protected readonly ordem = signal<'ALFABETICA' | 'VOTOS'>('ALFABETICA');
  protected readonly podioLiberado = signal(false);
  protected readonly votacaoAberta = signal(false);
  protected readonly totalVotos = computed(() => this.fotos().reduce((total, foto) => total + foto.votos, 0));

  async ngOnInit(): Promise<void> { await this.carregar(); }

  async carregar(): Promise<void> {
    this.carregando.set(true);
    try {
      const [fotos, podio, votacao] = await Promise.all([this.service.listarAdministracao(this.ordem()), this.service.podioAdministracao(), this.service.votacaoAdministracao()]);
      this.fotos.set(fotos); this.podioLiberado.set(podio); this.votacaoAberta.set(votacao);
    }
    catch (error) { this.erro.set(this.service.mensagemErro(error)); }
    finally { this.carregando.set(false); }
  }

  async alterarOrdem(ordem: string): Promise<void> {
    this.ordem.set(ordem === 'VOTOS' ? 'VOTOS' : 'ALFABETICA');
    await this.carregar();
  }

  async alterarPodio(liberado: boolean): Promise<void> {
    try { this.podioLiberado.set(await this.service.alterarPodio(liberado)); this.sucesso.set(liberado ? 'Pódio liberado para o público.' : 'Pódio ocultado do público.'); }
    catch (error) { this.erro.set(this.service.mensagemErro(error)); }
  }

  async alterarVotacao(aberta: boolean): Promise<void> {
    try { this.votacaoAberta.set(await this.service.alterarVotacao(aberta)); this.sucesso.set(aberta ? 'Votação ativada.' : 'Votação desativada. Quem ainda não votou não poderá votar.'); }
    catch (error) { this.erro.set(this.service.mensagemErro(error)); }
  }

  abrirCadastro(): void {
    this.limparFormulario();
    this.modalCadastro.set(true);
  }

  selecionarArquivo(event: Event): void {
    const file = (event.target as HTMLInputElement).files?.[0] ?? null;
    if (file && file.size > AdministracaoFotografiasComponent.TAMANHO_MAXIMO_FOTO) {
      this.arquivo.set(null);
      this.erro.set('A fotografia deve ter no máximo 10 MB.');
      (event.target as HTMLInputElement).value = '';
      if (this.preview()) URL.revokeObjectURL(this.preview()!);
      this.preview.set(null);
      return;
    }
    this.erro.set('');
    this.arquivo.set(file);
    if (this.preview()) URL.revokeObjectURL(this.preview()!);
    this.preview.set(file ? URL.createObjectURL(file) : null);
  }

  async cadastrar(): Promise<void> {
    const arquivo = this.arquivo();
    if (!this.titulo().trim() || !this.descricao().trim() || !arquivo) {
      this.erro.set('Preencha título, descrição e fotografia.'); return;
    }
    this.salvando.set(true); this.erro.set('');
    try {
      await this.service.criar(this.titulo(), this.descricao(), arquivo);
      this.modalCadastro.set(false); this.sucesso.set('Fotografia cadastrada.');
      await this.carregar();
    } catch (error) { this.erro.set(this.service.mensagemErro(error)); }
    finally { this.salvando.set(false); }
  }

  abrirEdicao(foto: FotografiaAdmin): void { this.editando.set({ ...foto }); }

  async salvarEdicao(): Promise<void> {
    const foto = this.editando(); if (!foto) return;
    this.salvando.set(true); this.erro.set('');
    try {
      await this.service.atualizar(foto); this.editando.set(null); this.sucesso.set('Alterações salvas.'); await this.carregar();
    } catch (error) { this.erro.set(this.service.mensagemErro(error)); }
    finally { this.salvando.set(false); }
  }

  async alterarStatus(foto: FotografiaAdmin, ativa: boolean): Promise<void> {
    try { await this.service.alterarStatus(foto.id, ativa); await this.carregar(); }
    catch (error) { this.erro.set(this.service.mensagemErro(error)); }
  }

  async excluir(foto: FotografiaAdmin): Promise<void> {
    if (!confirm(`Excluir definitivamente “${foto.titulo}”?`)) return;
    try { await this.service.excluir(foto.id); this.sucesso.set('Fotografia excluída.'); await this.carregar(); }
    catch (error) { this.erro.set(this.service.mensagemErro(error)); }
  }

  private limparFormulario(): void {
    this.titulo.set(''); this.descricao.set(''); this.arquivo.set(null); this.erro.set('');
    if (this.preview()) URL.revokeObjectURL(this.preview()!); this.preview.set(null);
  }

  ngOnDestroy(): void {
    this.fotos().forEach(foto => foto.imagemUrl && URL.revokeObjectURL(foto.imagemUrl));
    if (this.preview()) URL.revokeObjectURL(this.preview()!);
  }
}
